import { useEffect, useRef, useState } from "react";
import { X, Save, Trash2, RefreshCw, Plus, Loader2, Link2, MessageSquare, Clock, ThumbsUp, ThumbsDown, RotateCcw, FileText, Layers, Check, Stethoscope, CheckCircle2, Replace, Download, ListChecks, Image as ImageIcon, ExternalLink, Pencil, Scissors, ChevronLeft, ChevronRight, SkipBack } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import ShareModal from "@/components/ShareModal";
import UploadModal from "@/components/UploadModal";
import TagCombobox from "@/components/TagCombobox";
import PdfViewer from "@/components/PdfViewer";
import PdfAnnotationLayer from "@/components/PdfAnnotationLayer";
import { downloadBlob, buildAssetFilename, extForAsset } from "@/lib/downloadBlob";
import { clipTimecodes, fpsLabel, resolveFps } from "@/lib/timecode";
import { maybeNudgeIntoView } from "@/lib/scrollUtils";
import playerjs from "player.js";

function fmtBytes(b) {
  if (!b) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
}

// Iter 90 — Prev / Next / Go to start between clips of one request.
const navBtn = "flex items-center justify-center gap-1 whitespace-nowrap rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-200 hover:border-[var(--brand-accent)] hover:text-white transition-colors";
// Always rendered so the row never reflows; hidden slots keep their space.
function NavSlot({ show, testId, onClick, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-visible={show ? "true" : "false"}
      onClick={onClick}
      disabled={!show}
      aria-hidden={!show}
      tabIndex={show ? 0 : -1}
      className={`${navBtn} ${show ? "" : "invisible"}`}
    >
      {children}
    </button>
  );
}

function ClipNav({ nav }) {
  const { index, total, onPrev, onNext, onStart } = nav;
  const isFirst = index === 0;
  const isLast = index === total - 1;
  return (
    <div data-testid="clip-nav" className="mt-3">
      <div data-testid="clip-nav-position" className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
        Clip {index + 1} of {total}
      </div>
      <div className="grid grid-cols-3 gap-2">
      <NavSlot show={isLast} testId="clip-nav-start" onClick={onStart}>
        <SkipBack className="w-3 h-3" /> Go to start
      </NavSlot>
      <NavSlot show={!isFirst} testId="clip-nav-prev" onClick={onPrev}>
        <ChevronLeft className="w-3 h-3" /> Previous
      </NavSlot>
      <NavSlot show={!isLast} testId="clip-nav-next" onClick={onNext}>
        Next <ChevronRight className="w-3 h-3" />
      </NavSlot>
      </div>
    </div>
  );
}

function fmtTs(sec) {
  if (sec === null || sec === undefined || isNaN(sec) || sec < 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function timeAgoShort(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtFullDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (e) { return iso; }
}

export default function AssetDetailModal({ assetId, onClose, onChange, initialSeekSec, clipRequest }) {
  const { user } = useAuth();
  const clipTc = clipRequest ? clipTimecodes(clipRequest) : null;
  // Clients can edit tags/description within their scope (backend enforces the
  // scope, we just have to let the form render). Iter 27 unlocked them.
  const canEdit = ["admin", "editor", "client"].includes(user?.role);
  // Iter 28 — tagging-only skin for clients. Hides every management action,
  // makes title/description read-only, and auto-saves tag mutations.
  const isClient = user?.role === "client";
  const [asset, setAsset] = useState(null);
  const [embed, setEmbed] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  // Iter 47 — imperative handle for the PDF viewer so comment "Page N" pills
  // can smooth-scroll the reader to the referenced page.
  const pdfViewerRef = useRef(null);
  // Iter 42 — Dirty-tracking snapshots. Captured on load / after every
  // successful save. `save()` diffs against these to send only mutated
  // fields, so idempotent Save clicks never bump `last_tagged_at`.
  const [initialTitle, setInitialTitle] = useState("");
  const [initialDescription, setInitialDescription] = useState("");
  const [initialTags, setInitialTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const [dlQualities, setDlQualities] = useState(null);
  const [dlBusy, setDlBusy] = useState(false);
  const [dlError, setDlError] = useState("");
  // Iter 44 — per-row saving state for direct-download blob fetches.
  // Keyed by resolution ("original", "75%", …) so multiple sizes can be
  // clicked in sequence without stomping on each other's spinners.
  const [dlSaving, setDlSaving] = useState({});

  // Iter 44 — Direct download via fetch-to-blob. Forces the browser to save
  // the file instead of opening it in a new tab. Filename shape:
  //   {sanitized_title}_{Original|75pc|50pc|25pc}.{ext}
  const handleDirectDownload = async (quality) => {
    if (!asset) return;
    const key = quality?.resolution || "file";
    setDlSaving((p) => ({ ...p, [key]: true }));
    const filename = buildAssetFilename({
      title: asset.title,
      resolution: quality?.resolution,
      ext: extForAsset(asset),
    });
    try {
      await downloadBlob(quality.url, filename);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Download failed — try again");
    } finally {
      setDlSaving((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  };

  // Iter 44 — Single-shot PDF direct download (bypasses the size menu since
  // PDFs have no resolution variants).
  const handlePdfDirectDownload = async () => {
    if (!asset?.cdn_url) return;
    const filename = buildAssetFilename({
      title: asset.title,
      resolution: null,
      ext: extForAsset(asset),
    });
    setDlSaving((p) => ({ ...p, pdf: true }));
    try {
      await downloadBlob(asset.cdn_url, filename);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Download failed — try again");
    } finally {
      setDlSaving((p) => {
        const next = { ...p };
        delete next.pdf;
        return next;
      });
    }
  };

  const loadDownloadQualities = async () => {
    if (dlQualities) { setDlOpen((p) => !p); return; }
    setDlBusy(true); setDlError("");
    try {
      // Iter 43 — photo assets use Bunny Optimizer variants; videos use MP4 renditions.
      const endpoint = asset?.kind === "photo"
        ? `/assets/${activeAssetId}/image-qualities`
        : `/assets/${activeAssetId}/qualities`;
      const { data } = await api.get(endpoint);
      setDlQualities(data.qualities || []);
      setDlOpen(true);
    } catch (e) {
      setDlError(e.response?.data?.detail || "Could not load download qualities");
      setDlOpen(true);
    } finally {
      setDlBusy(false);
    }
  };
  const [comments, setComments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showCollectionMenu, setShowCollectionMenu] = useState(false);
  // Iter 50 — Version Stacking. `versions` is the full ladder for this
  // asset's group (ordered by version_number asc). `activeAssetId` lets the
  // user click a V1|V2|V3 chip to swap the modal to a different version
  // without closing/reopening it. `uploadingVersion` gates the file input
  // so double-clicks don't queue duplicate uploads.
  const [versions, setVersions] = useState([]);
  const [activeAssetId, setActiveAssetId] = useState(assetId);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const versionFileInputRef = useRef(null);
  // Iter 51 — View-level "Show history from previous versions" toggle.
  // Non-destructive: comments stay per-version on disk; when this flips on,
  // /assets/{id}/comments?include_all_versions=true aggregates the whole
  // group and each row carries its origin `version_number` for the badge.
  const [showAllVersionComments, setShowAllVersionComments] = useState(false);
  // Iter 52 — "This Page Only" filter + annotation playback state.
  const [currentPage, setCurrentPage] = useState(1);
  const [thisPageOnly, setThisPageOnly] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  // Iter 54 — Video player state for time-locked stroke playback + frame-by-frame scrubbing.
  const videoIframeRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoIsPaused, setVideoIsPaused] = useState(true);
  // Iter 59 — Frame-1 ghost guard. Stays false until Bunny confirms a real
  // playhead via timeupdate/pause. Reset whenever the modal opens on a
  // different asset (photo → video swap etc).
  const [videoTimeKnown, setVideoTimeKnown] = useState(false);
  useEffect(() => {
    setVideoCurrentTime(0);
    setVideoIsPaused(true);
    setVideoTimeKnown(false);
  }, [asset?.id]);
  const onVideoIframeLoad = () => {
    const iframe = videoIframeRef.current;
    if (!iframe) return;
    try {
      const player = new playerjs.Player(iframe);
      videoPlayerRef.current = player;
      player.on("ready", () => {
        // Iter 75 — initial seek: honor the `initialSeekSec` prop set by the
        // Clip Requests "Jump to clip" flow so admins land at the In point.
        try {
          if (typeof initialSeekSec === "number" && initialSeekSec > 0) {
            player.setCurrentTime(initialSeekSec);
            player.pause();
          }
        } catch (e) { /* player not ready — silent no-op */ }
        player.on("timeupdate", (d) => {
          if (d && typeof d.seconds === "number") {
            setVideoCurrentTime(d.seconds);
            setVideoTimeKnown(true);
          }
        });
        player.on("play", () => setVideoIsPaused(false));
        player.on("pause", () => {
          setVideoIsPaused(true);
          setVideoTimeKnown(true);
          // Iter 58 — Focus Hijack. Bunny iframe swallows the next arrow
          // press after a click-pause; force focus back to the parent so our
          // 0.04167s frame-scrub logic wins immediately.
          try { window.focus(); } catch (e) {}
          try { videoIframeRef.current?.blur(); } catch (e) {}
          try {
            const ae = document.activeElement;
            if (ae && ae !== document.body) ae.blur?.();
          } catch (e) {}
        });
      });
    } catch (e) { /* postMessage bridge failed — playback still works, just no time-lock */ }
  };
  // Iter 61 — Hardened seek path (parity with ShareView `seekTo`).
  // Tries player.js `setCurrentTime` first; if the postMessage bridge isn't
  // ready or throws, falls back to reloading the Bunny iframe with a `?t=`
  // query so the timestamp click always lands. Uses `maybeNudgeIntoView`
  // (shared with ShareView) so the viewer stays flush without pinning the
  // parent header off-screen.
  const seekAndPauseVideo = (t) => {
    if (typeof t !== "number") return;
    const player = videoPlayerRef.current;
    if (player) {
      try {
        player.setCurrentTime(t);
        player.pause();
        maybeNudgeIntoView(videoIframeRef.current);
        return;
      } catch (e) { /* fall through to iframe reload */ }
    }
    const iframe = videoIframeRef.current;
    if (!iframe || !iframe.src) return;
    try {
      const url = new URL(iframe.src);
      url.searchParams.set("t", Math.floor(t));
      url.searchParams.set("autoplay", "false");
      iframe.src = url.toString();
      maybeNudgeIntoView(iframe);
    } catch (e) { /* malformed src — silent no-op */ }
  };
  // Iter 54 — Frame-by-frame arrow keys (24 fps).
  // Iter 57 — Frame step pinned to exactly 0.04167s (24fps math), matching
  // the ShareView composer so admin playback scrubs in the same cadence
  // reviewers use to author annotations.
  // Iter 56 — Blur the Bunny iframe on every arrow press. The iframe is
  // cross-origin, so parent-window preventDefault can't cancel its internal
  // ±5s seek handler; moving focus off is the only reliable override.
  const ADMIN_FRAME_STEP = 0.04167;
  useEffect(() => {
    if (asset?.kind !== "video") return;
    const onKey = (ev) => {
      if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
      const t = ev.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!videoIsPaused) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      try { videoIframeRef.current?.blur(); } catch (e) {}
      try {
        const ae = document.activeElement;
        if (ae === videoIframeRef.current) ae.blur?.();
      } catch (e) {}
      const delta = ev.key === "ArrowRight" ? ADMIN_FRAME_STEP : -ADMIN_FRAME_STEP;
      const next = Math.max(0, videoCurrentTime + delta);
      try {
        videoPlayerRef.current?.setCurrentTime(next);
        videoPlayerRef.current?.pause();
      } catch (e) {}
      setVideoCurrentTime(next);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [asset, videoIsPaused, videoCurrentTime]);

  // Reset activeAssetId whenever the parent passes a new assetId (e.g. a
  // different card was clicked in the grid).
  useEffect(() => { setActiveAssetId(assetId); }, [assetId]);

  useEffect(() => {
    if (!activeAssetId) return;
    api.get(`/assets/${activeAssetId}`).then((r) => {
      setAsset(r.data);
      setTitle(r.data.title);
      setDescription(r.data.description || "");
      setTags(r.data.tags || []);
      setInitialTitle(r.data.title || "");
      setInitialDescription(r.data.description || "");
      setInitialTags(r.data.tags || []);
      if ((r.data.kind || "video") === "video") {
        api.get(`/assets/${activeAssetId}/embed`).then((e) => setEmbed(e.data.embed_url)).catch(() => {});
      } else {
        setEmbed("");
      }
    });
    setDlQualities(null);
    setDlOpen(false);
    // Load comments respecting the current history toggle so switching
    // versions preserves the viewer's chosen scope.
    api.get(`/assets/${activeAssetId}/comments`, {
      params: showAllVersionComments ? { include_all_versions: "true" } : {},
    }).then((r) => setComments(r.data)).catch(() => setComments([]));
    api.get(`/assets/${activeAssetId}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
    // eslint-disable-next-line
  }, [activeAssetId, showAllVersionComments]);

  const loadComments = async () => {
    try {
      const r = await api.get(`/assets/${activeAssetId}/comments`, {
        params: showAllVersionComments ? { include_all_versions: "true" } : {},
      });
      setComments(r.data);
    } catch (e) { /* no comments yet */ }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this client comment? This cannot be undone.")) return;
    try {
      await api.delete(`/comments/${commentId}`);
      await loadComments();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not delete comment");
    }
  };

  const toggleResolved = async (commentId, currentlyResolved) => {
    try {
      await api.patch(`/comments/${commentId}/resolve`, { resolved: !currentlyResolved });
      await loadComments();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not update");
    }
  };

  const [healthState, setHealthState] = useState(null); // null | {loading, ok, hint, status}
  const runEmbedHealth = async () => {
    setHealthState({ loading: true });
    try {
      const { data } = await api.get(`/admin/assets/${activeAssetId}/embed-health`);
      setHealthState({ loading: false, ...data });
    } catch (e) {
      setHealthState({ loading: false, status: "error", hint: e.response?.data?.detail || "Check failed" });
    }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("wd_token");
      const res = await fetch(`${API}/assets/${activeAssetId}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${asset?.title || "report"} - feedback.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not generate report.");
    }
  };

  const loadCollections = async () => {
    try {
      const { data } = await api.get("/collections");
      setCollections(data);
    } catch { /* ignore */ }
  };

  const toggleInCollection = async (cid, currentlyIn) => {
    try {
      if (currentlyIn) {
        await api.delete(`/collections/${cid}/assets/${activeAssetId}`);
      } else {
        await api.post(`/collections/${cid}/assets/${activeAssetId}`);
      }
      await loadCollections();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not update collection");
    }
  };

  useEffect(() => { if (showCollectionMenu) loadCollections(); }, [showCollectionMenu]);

  // Auto-poll if processing — videos only. Photos/PDFs are always ready.
  useEffect(() => {
    if (!asset || (asset.kind || "video") !== "video") return;
    if (asset.status === "ready" || asset.status === "failed") return;
    const t = setInterval(async () => {
      try {
        const r = await api.post(`/assets/${asset.id}/refresh-status`);
        setAsset(r.data);
        if (r.data.status === "ready") {
          const e = await api.get(`/assets/${asset.id}/embed`);
          setEmbed(e.data.embed_url);
          onChange?.();
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 5000);
    return () => clearInterval(t);
  }, [asset?.status, asset?.id, onChange]);

  const save = async () => {
    // Iter 42 — Only send dirty fields. Prevents idempotent Save clicks from
    // touching `last_tagged_at` (tags) or `updated_at` (any field) server-side.
    const body = {};
    if (title !== initialTitle) body.title = title;
    if (description !== initialDescription) body.description = description;
    const sortedInitial = [...initialTags].sort();
    const sortedCurrent = [...tags].sort();
    if (JSON.stringify(sortedInitial) !== JSON.stringify(sortedCurrent)) {
      body.tags = tags;
    }
    if (Object.keys(body).length === 0) {
      // Nothing changed — skip the PATCH entirely to keep the pip honest.
      toast.success("No changes to save");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/assets/${activeAssetId}`, body);
      setAsset(r.data);
      // Re-seed snapshots so subsequent Saves diff against the newly-saved state.
      setInitialTitle(r.data.title || "");
      setInitialDescription(r.data.description || "");
      setInitialTags(r.data.tags || []);
      onChange?.();
    } finally {
      setSaving(false);
    }
  };

  // Client-only inline tag persister. Called every time a client adds or
  // removes a tag; no debounce needed since tag mutations are discrete clicks.
  // Shows a subtle "Saved ✓" pip for 1.5s on success; toasts on error only.
  const [tagSaveState, setTagSaveState] = useState("");  // "" | "saving" | "saved" | "error"
  const saveTagsInline = async (nextTags) => {
    setTagSaveState("saving");
    try {
      await api.patch(`/assets/${activeAssetId}`, { tags: nextTags });
      // Iter 42 — Keep the dirty-tracking snapshot in sync after each inline
      // save so the non-client Save path (if ever mixed) never re-sends stale tags.
      setInitialTags(nextTags);
      setTagSaveState("saved");
      onChange?.();
      setTimeout(() => setTagSaveState((s) => (s === "saved" ? "" : s)), 1500);
    } catch (e) {
      setTagSaveState("error");
      toast.error(e.response?.data?.detail || "Couldn't save tags — try again");
    }
  };

  const remove = async () => {
    if (!confirm("Delete this video? This removes it from Bunny too.")) return;
    await api.delete(`/assets/${activeAssetId}`);
    onChange?.();
    onClose();
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/assets/${activeAssetId}/refresh-status`);
      setAsset(r.data);
      if (r.data.status === "ready") {
        const e = await api.get(`/assets/${activeAssetId}/embed`);
        setEmbed(e.data.embed_url);
      }
      onChange?.();
    } finally {
      setRefreshing(false);
    }
  };

  // Iter 50 — Upload a new version. Photos/PDFs go through the single-shot
  // multipart endpoint. Videos need to route through the TUS init flow —
  // we open the existing Replace/ReplaceModal path there. On success we
  // swap the modal to the newly uploaded version's asset_id.
  const handleUploadNewVersionClick = () => {
    if (!asset) return;
    if (asset.kind === "video") {
      // Reuse the existing Replace modal for videos; it already knows the
      // Bunny TUS flow. On success it triggers onChange which refreshes the
      // grid. Version behaviour (is_latest flip, group_id inherit) is
      // handled server-side by /versions/init-upload once wired.
      setShowReplace(true);
      return;
    }
    versionFileInputRef.current?.click();
  };

  const handleVersionFileChange = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // reset for subsequent picks
    if (!file || !asset) return;
    setUploadingVersion(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("wd_token");
      const res = await fetch(`${API}/assets/${activeAssetId}/versions/upload-file`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      const newAsset = await res.json();
      toast.success(`Uploaded V${newAsset.version_number}`);
      // Swap the modal to the newly created version and refresh the parent grid.
      setActiveAssetId(newAsset.id);
      onChange?.();
    } catch (e) {
      toast.error(e.message || "Version upload failed");
    } finally {
      setUploadingVersion(false);
    }
  };

  if (!asset) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-accent)]" />
      </div>
    );
  }

  return (
    <div
      data-testid={TID.assetModal}
      className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 md:p-8 grain-overlay"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl max-h-[92vh] bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid={TID.closeAssetModal}
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 flex items-center justify-center text-zinc-400 hover:text-white transition"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Left: media viewer — kind-aware */}
        <div className="md:flex-1 bg-black flex items-center justify-center min-h-[40vh] md:min-h-0 p-4">
          {asset.kind === "photo" ? (
            // Iter 53 — Photo playback with annotation overlay. Admin is
            // playback-only (feedback is authored via the client share).
            <div className="relative max-w-full max-h-full flex items-center justify-center">
              <img
                data-testid="asset-photo-viewer"
                src={asset.preview_url || asset.cdn_url}
                alt={asset.title}
                className="max-w-full max-h-full object-contain"
              />
              <PdfAnnotationLayer
                pageNumber={1}
                strokes={comments
                  .filter((c) => c.drawing?.strokes?.length)
                  .flatMap((c) => (c.drawing.strokes || []).map((s, i) => ({
                    tool: s.tool, points: s.points, commentId: c.id, id: `${c.id}-${i}`,
                  })))}
                interactive={false}
                highlightedCommentIds={highlightedCommentId ? [highlightedCommentId] : []}
              />
            </div>
          ) : asset.kind === "pdf" ? (
            <div className="w-full h-full flex flex-col min-h-[60vh]">
              <PdfViewer
                ref={pdfViewerRef}
                url={asset.cdn_url}
                title={asset.title}
                onPageChange={setCurrentPage}
                renderPageOverlay={(pageNum, pageWidthPx) => {
                  // Iter 52 — Playback strokes for THIS page. Admin never
                  // captures new annotations (they're client-side feedback),
                  // so `interactive` is always false here.
                  const strokesForPage = comments
                    .filter((c) => c.drawing?.strokes?.length && c.page_number === pageNum)
                    .flatMap((c) => (c.drawing.strokes || []).map((s, i) => ({
                      tool: s.tool,
                      points: s.points,
                      commentId: c.id,
                      id: `${c.id}-${i}`,
                    })));
                  return (
                    <PdfAnnotationLayer
                      pageNumber={pageNum}
                      pageWidth={pageWidthPx}
                      strokes={strokesForPage}
                      interactive={false}
                      highlightedCommentIds={highlightedCommentId ? [highlightedCommentId] : []}
                    />
                  );
                }}
              />
            </div>
          ) : asset.status === "ready" && embed ? (
            <div className="w-full max-h-full aspect-video relative">
              <iframe
                ref={videoIframeRef}
                src={embed}
                onLoad={onVideoIframeLoad}
                className="w-full h-full block"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                title={asset.title}
              />
              {/* Iter 54 — Admin playback of time-locked video annotations.
                  Admin never draws here (feedback is authored via the client
                  share), so interactive is always false. */}
              <PdfAnnotationLayer
                pageNumber={1}
                strokes={comments
                  // Iter 59 — gate on videoTimeKnown so admin preview never
                  // paints strokes before Bunny reports a real playhead
                  // (Frame-1 ghost fix, mirrors ShareView).
                  .filter((c) => c.drawing?.strokes?.length && typeof c.timestamp_sec === "number"
                             && videoTimeKnown && videoIsPaused
                             && Math.abs(c.timestamp_sec - videoCurrentTime) < 0.5)
                  .flatMap((c) => (c.drawing.strokes || []).map((s, i) => ({
                    tool: s.tool, color: s.color, points: s.points, commentId: c.id, id: `${c.id}-${i}`,
                  })))}
                interactive={false}
                highlightedCommentIds={highlightedCommentId ? [highlightedCommentId] : []}
              />
              {/* Iter 78 — Clip Request timeline highlight. Only renders
                  when the modal is opened via "Jump to clip" from the
                  admin page (clipRequest prop is set) AND the asset has
                  a known duration. A thin bar is overlaid on the bottom
                  edge of the iframe, positioned proportionally to the
                  [in, out] range so admins can see the exact clip range
                  at a glance without scrubbing. */}
              {clipRequest && asset?.duration_sec > 0 && (() => {
                const dur = asset.duration_sec;
                const left = Math.max(0, Math.min(100, (clipRequest.inSec / dur) * 100));
                const width = Math.max(1, Math.min(100 - left, ((clipRequest.outSec - clipRequest.inSec) / dur) * 100));
                return (
                  <div
                    data-testid="clip-request-timeline-highlight"
                    className="pointer-events-none absolute left-0 right-0 bottom-0 h-2 bg-black/60"
                    aria-hidden
                  >
                    <div className="absolute inset-y-0 bg-[var(--brand-accent)]/15" style={{ left: 0, width: "100%" }} />
                    <div
                      className="absolute inset-y-0 bg-[var(--brand-accent)] shadow-[0_0_8px_var(--brand-accent)]"
                      style={{ left: `${left}%`, width: `${width}%` }}
                    />
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="flex flex-col items-center text-zinc-500 p-12">
              {asset.status === "failed" ? (
                <>
                  <div className="text-red-400 text-sm">Encoding failed</div>
                  <button onClick={refresh} className="text-xs text-[var(--brand-accent)] mt-2 hover:underline">Try refresh</button>
                </>
              ) : (
                <>
                  <div className="spinner mb-3" />
                  <div className="text-xs uppercase tracking-wider">{asset.status === "uploading" ? "Uploading" : "Encoding on Bunny.net"}</div>
                  <div className="text-[11px] text-zinc-600 mt-1">This usually takes 1–3 minutes</div>
                  <button
                    data-testid={TID.refreshStatusBtn}
                    onClick={refresh}
                    disabled={refreshing}
                    className="mt-4 text-xs text-zinc-400 hover:text-[var(--brand-accent)] flex items-center gap-1.5"
                  >
                    <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh now
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Right: metadata */}
        <div className="md:w-[420px] bg-zinc-950 border-l border-zinc-800/60 overflow-y-auto p-6 space-y-5">
          {/* Iter 78 — Clip Request context banner. Only renders when the
              modal was opened via "Jump to clip" from the admin page
              (clipRequest prop set). Mirrors the admin list row so the
              reviewer never loses the requester / in-out / note context
              while inside the modal. */}
          {clipRequest && (
            <div
              data-testid="clip-request-context-banner"
              className="rounded-lg border border-[var(--brand-accent)]/50 bg-[var(--brand-accent)]/5 p-3"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--brand-accent)] mb-2">
                <Scissors className="w-3 h-3" /> Clip Request
              </div>
              {clipRequest.requester && (
                <div className="text-xs text-zinc-300">
                  Requested by <span className="font-semibold text-zinc-100">{clipRequest.requester}</span>
                </div>
              )}
              <div className="text-[11px] text-zinc-400 tabular-nums mt-1">
                In{" "}
                <button
                  type="button"
                  data-testid="clip-request-banner-in"
                  onClick={() => seekAndPauseVideo(clipRequest.inSec)}
                  title="Seek video to In point"
                  className="text-[var(--brand-accent)] hover:underline underline-offset-2 decoration-dotted hover:decoration-solid focus:outline-none focus:ring-1 focus:ring-[var(--brand-accent)]/40 rounded px-0.5"
                >
                  {clipTc.inTc}
                </button>
                {" → "}
                Out{" "}
                <button
                  type="button"
                  data-testid="clip-request-banner-out"
                  onClick={() => seekAndPauseVideo(clipRequest.outSec)}
                  title="Seek video to Out point"
                  className="text-[var(--brand-accent)] hover:underline underline-offset-2 decoration-dotted hover:decoration-solid focus:outline-none focus:ring-1 focus:ring-[var(--brand-accent)]/40 rounded px-0.5"
                >
                  {clipTc.outTc}
                </button>
                <span className="text-zinc-600" data-testid="clip-request-banner-length"> · {clipTc.length}</span>
                {clipTc.fpsText && <span className="text-zinc-600" data-testid="clip-request-banner-fps"> · {clipTc.fpsText}</span>}
              </div>
              {clipRequest.note && (
                <blockquote className="mt-2 border-l-2 border-[var(--brand-accent)]/40 pl-2 text-[11px] italic text-zinc-300">
                  “{clipRequest.note}”
                </blockquote>
              )}
              {clipRequest.nav && <ClipNav nav={clipRequest.nav} />}
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Title</label>
            <input
              data-testid={TID.assetTitleInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!canEdit || isClient}
              readOnly={isClient}
              className={`w-full mt-1 border-b py-1.5 text-lg font-heading font-semibold tracking-tight focus:outline-none ${isClient ? "bg-transparent border-zinc-900 text-zinc-400 cursor-default" : "bg-transparent border-zinc-800 focus:border-[var(--brand-accent)] disabled:opacity-60"}`}
            />
          </div>

          {/* Iter 50 — Version switcher. Only rendered when the asset has more
              than one version. Clicking a chip re-loads the modal against that
              version's asset_id. The chip for the active version is highlighted
              amber and shows "· LATEST" when applicable. */}
          {versions.length > 1 && (
            <div data-testid="version-switcher">
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Versions</label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {versions.map((v) => {
                  const active = v.id === activeAssetId;
                  return (
                    <button
                      key={v.id}
                      data-testid={`version-chip-v${v.version_number}`}
                      onClick={() => setActiveAssetId(v.id)}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${active
                        ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                        : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-[var(--brand-accent)]/40 hover:text-white"}`}
                      title={v.is_latest ? "Latest version" : `Version ${v.version_number}`}
                    >
                      V{v.version_number}
                      {v.is_latest && !active && (
                        <span className="text-[8px] uppercase tracking-wider text-emerald-400">· latest</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {!asset.is_latest && (
                <div className="mt-2 text-[10px] text-amber-400/80 flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  You're viewing an older version.
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Description</label>
            <textarea
              data-testid={TID.assetDescInput}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit || isClient}
              readOnly={isClient}
              rows={3}
              placeholder={isClient ? "" : "Add a description..."}
              className={`w-full mt-1 border rounded-md px-3 py-2 text-sm focus:outline-none resize-none ${isClient ? "bg-black/20 border-zinc-900 text-zinc-400 cursor-default" : "bg-black/40 border-zinc-800 focus:border-[var(--brand-accent)]/60 disabled:opacity-60"}`}
            />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex-1">Tags ({tags.length})</label>
              {isClient && tagSaveState === "saving" && (
                <span data-testid="tag-save-saving" className="text-[10px] text-zinc-500 flex items-center gap-1">
                  <Loader2 className="w-2.5 h-2.5 animate-spin" /> Saving…
                </span>
              )}
              {isClient && tagSaveState === "saved" && (
                <span data-testid="tag-save-saved" className="text-[10px] text-emerald-400 flex items-center gap-1 fade-out">
                  <Check className="w-2.5 h-2.5" /> Saved
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {tags.map((t) => (
                <span
                  key={t}
                  data-testid={TID.tagChip(t)}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-300 bg-zinc-900 border border-zinc-800 px-2 py-1 rounded-full"
                >
                  {t}
                  {canEdit && (
                    <button
                      data-testid={TID.removeTag(t)}
                      onClick={() => {
                        const next = tags.filter((x) => x !== t);
                        setTags(next);
                        if (isClient) saveTagsInline(next);
                      }}
                      className="text-zinc-600 hover:text-red-400"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {canEdit && (
              <div className="flex gap-2 mt-2">
                <TagCombobox
                  existing={tags}
                  onAdd={(t) => {
                    const next = [...tags, t];
                    setTags(next);
                    if (isClient) saveTagsInline(next);
                  }}
                  testId={TID.tagInput}
                  placeholder="Add a tag…"
                  folderId={asset.folder_id}
                />
              </div>
            )}
          </div>

          {(comments.length > 0 || versions.length > 1) && (
            <div className="pt-4 border-t border-zinc-800/60">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <MessageSquare className="w-3.5 h-3.5 text-[var(--brand-accent)] shrink-0" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Client feedback</span>
                  {canEdit && comments.length > 0 && (
                    <span
                      data-testid="comments-progress-pill"
                      className="text-[10px] text-emerald-400 bg-emerald-950/30 border border-emerald-900/50 px-1.5 py-0.5 rounded-full"
                    >
                      {comments.filter((c) => c.resolved && !c.parent_comment_id).length} / {comments.filter((c) => !c.parent_comment_id).length} done
                    </span>
                  )}
                  {/* Iter 51 — Inline "Show history" pill. Only meaningful
                      when the group has >1 version; otherwise there's no
                      history to show. Non-destructive view toggle. */}
                  {versions.length > 1 && (
                    <button
                      type="button"
                      data-testid="show-all-versions-toggle"
                      onClick={() => setShowAllVersionComments((v) => !v)}
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition uppercase tracking-wider ${showAllVersionComments
                        ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                        : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                      title={showAllVersionComments ? "Hide comments from other versions" : "Show comments from all versions"}
                    >
                      <Layers className="w-2.5 h-2.5" /> History
                    </button>
                  )}
                  {/* Iter 52 — "This Page Only" — filter feedback to comments
                      anchored to the currently visible PDF page. */}
                  {asset.kind === "pdf" && (
                    <button
                      type="button"
                      data-testid="this-page-only-toggle"
                      onClick={() => setThisPageOnly((v) => !v)}
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition uppercase tracking-wider ${thisPageOnly
                        ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                        : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                      title={thisPageOnly ? "Show feedback from all pages" : "Show only feedback on this page"}
                    >
                      <FileText className="w-2.5 h-2.5" /> This page
                    </button>
                  )}
                  {/* Iter 54 — "This Time Only" for video: filters comments to
                      those anchored within ±2s of the current playhead. */}
                  {asset.kind === "video" && (
                    <button
                      type="button"
                      data-testid="this-time-only-toggle"
                      onClick={() => setThisPageOnly((v) => !v)}
                      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition uppercase tracking-wider ${thisPageOnly
                        ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]"
                        : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/50 hover:text-[var(--brand-accent)]"}`}
                      title={thisPageOnly ? "Show feedback from the whole video" : "Show only feedback near this moment (±2s)"}
                    >
                      <Clock className="w-2.5 h-2.5" /> This time
                    </button>
                  )}
                </div>
                {canEdit && (
                  <a
                    data-testid="open-checklist-link"
                    href={`/assets/${activeAssetId}/checklist`}
                    className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[var(--brand-accent)] flex items-center gap-1 transition shrink-0"
                    title="Open dedicated checklist workspace"
                  >
                    <ListChecks className="w-3 h-3" /> Checklist
                  </a>
                )}
              </div>
              {(() => {
                // Iter 52 / 54 — visible top-level admin comments, respecting
                //   • "This page only" (PDF) → c.page_number === currentPage
                //   • "This time only" (video) → |c.timestamp_sec − videoCurrentTime| < 2s
                const topLevel = comments.filter((c) => !c.parent_comment_id);
                let visible = topLevel;
                if (thisPageOnly && asset.kind === "pdf") {
                  visible = topLevel.filter((c) => c.page_number === currentPage);
                } else if (thisPageOnly && asset.kind === "video") {
                  visible = topLevel.filter((c) => typeof c.timestamp_sec === "number" && Math.abs(c.timestamp_sec - videoCurrentTime) < 2);
                }
                if (visible.length === 0) {
                  return (
                    <div className="text-[11px] text-zinc-500 italic pl-6">
                      {thisPageOnly && asset.kind === "pdf" ? `No feedback on page ${currentPage}.`
                        : thisPageOnly && asset.kind === "video" ? `No feedback near this moment.`
                        : "No feedback on this version yet."}
                    </div>
                  );
                }
                return (
                  <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1">
                    {visible.map((c) => (
                      <AgencyComment
                        key={c.id}
                        c={c}
                        replies={comments.filter((r) => r.parent_comment_id === c.id)}
                        canEdit={canEdit}
                        assetId={activeAssetId}
                        activeVersionNumber={asset.version_number}
                        onJumpToPage={(p) => pdfViewerRef.current?.scrollToPage(p)}
                        onSeekAndPause={seekAndPauseVideo}
                        onHighlight={() => {
                          setHighlightedCommentId(c.id);
                          setTimeout(() => setHighlightedCommentId((cur) => (cur === c.id ? null : cur)), 2000);
                        }}
                        onDelete={() => deleteComment(c.id)}
                        onToggleResolved={() => toggleResolved(c.id, c.resolved)}
                        onReplied={refresh}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="pt-4 border-t border-zinc-800/60 space-y-1.5 text-xs">
            <Row
              label="Status"
              value={
                asset.first_downloaded_at ? (
                  <span className="uppercase tracking-wider text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Delivered
                  </span>
                ) : (
                  <span className={`uppercase tracking-wider ${asset.status === "ready" ? "text-emerald-400" : asset.status === "failed" ? "text-red-400" : "text-[var(--brand-accent)]"}`}>
                    {asset.status}
                  </span>
                )
              }
            />
            <Row label="Duration" value={asset.duration_sec ? `${Math.floor(asset.duration_sec / 60)}:${String(Math.floor(asset.duration_sec % 60)).padStart(2, "0")}` : "—"} />
            <Row label="Resolution" value={asset.width ? `${asset.width} × ${asset.height}` : "—"} />
            {(asset.kind || "video") === "video" && (
              <Row label="Frame rate" value={<span data-testid="asset-frame-rate">{fpsLabel(resolveFps(asset.frame_rate))}</span>} />
            )}
            <Row label="Size" value={fmtBytes(asset.size_bytes)} />
            <Row label="Uploaded" value={fmtFullDate(asset.created_at)} />
            {asset.last_replaced_at && (
              <Row label="Last replaced" value={<span className="text-zinc-200">{fmtFullDate(asset.last_replaced_at)}{asset.version_count > 1 && <span className="text-zinc-600 ml-1.5">· v{asset.version_count}</span>}</span>} />
            )}
            {asset.first_downloaded_at && (
              <Row
                label="Delivered"
                value={
                  <span className="text-emerald-400 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" />
                    {fmtFullDate(asset.first_downloaded_at)}
                    {user?.role === "admin" && (
                      <button
                        onClick={async () => {
                          if (!window.confirm("Reset the delivered status? The asset will show as awaiting delivery again until the next share-side download.")) return;
                          try {
                            await api.post(`/assets/${asset.id}/reset-delivery`);
                            await refresh();
                            onChange?.();
                          } catch (e) {
                            alert(e.response?.data?.detail || "Could not reset delivery");
                          }
                        }}
                        data-testid="reset-delivery-btn"
                        className="ml-1 text-zinc-500 hover:text-red-400 transition"
                        title="Reset delivered status"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                }
              />
            )}
            {!isClient && asset.kind === "video" && (
              <Row label="Bunny ID" value={<span className="font-mono text-[10px]">{asset.bunny_video_id?.slice(0, 8)}…</span>} />
            )}
            {!isClient && (asset.kind === "photo" || asset.kind === "pdf") && asset.size_bytes > 0 && (
              <Row label="Type" value={<span className="uppercase tracking-wider">{asset.content_type || asset.kind}</span>} />
            )}          </div>

          {canEdit && !isClient && (
            <div className="pt-4 border-t border-zinc-800/60 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid={TID.shareAssetBtn}
                  onClick={() => setShowShare(true)}
                  disabled={asset.status !== "ready"}
                  className="bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  <Link2 className="w-3.5 h-3.5 text-[var(--brand-accent)]" /> Share
                </button>
                <div className="relative">
                  <button
                    data-testid={TID.addToCollectionBtn}
                    onClick={() => setShowCollectionMenu((p) => !p)}
                    className="w-full bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5"
                  >
                    <Layers className="w-3.5 h-3.5 text-[var(--brand-accent)]" /> Collection
                  </button>
                  {showCollectionMenu && (
                    <div className="absolute right-0 top-full mt-1 w-64 max-h-64 overflow-y-auto bg-zinc-950 border border-zinc-800 rounded-md p-1 z-20 shadow-2xl">
                      {collections.length === 0 ? (
                        <div className="text-[10px] text-zinc-500 px-2 py-3 text-center">No collections yet. Create one on the Collections page.</div>
                      ) : (
                        collections.map((c) => {
                          const inIt = c.asset_ids.includes(activeAssetId);
                          // Iter 65 — Smart collections are auto-managed by
                          // tags; disable manual add/remove and hint why.
                          if (c.is_smart) {
                            return (
                              <div
                                key={c.id}
                                data-testid={`collection-menu-smart-${c.id}`}
                                title="Smart — auto-managed by tags"
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-zinc-500 rounded cursor-not-allowed"
                              >
                                {inIt ? <Check className="w-3 h-3 text-[var(--brand-accent)]/60" /> : <span className="w-3" />}
                                <span className="truncate flex-1">{c.name}</span>
                                <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/70">Smart</span>
                              </div>
                            );
                          }
                          return (
                            <button
                              key={c.id}
                              onClick={() => toggleInCollection(c.id, inIt)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900 rounded text-left"
                            >
                              {inIt ? <Check className="w-3 h-3 text-[var(--brand-accent)]" /> : <span className="w-3" />}
                              <span className="truncate flex-1">{c.name}</span>
                              <span className="text-[9px] text-zinc-600">{c.asset_count}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                data-testid={TID.reportAssetBtn}
                onClick={downloadReport}
                disabled={comments.length === 0}
                className="w-full bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                title={comments.length === 0 ? "No feedback to report yet" : "Download branded PDF of all feedback"}
              >
                <FileText className="w-3.5 h-3.5 text-[var(--brand-accent)]" /> Download feedback PDF
              </button>
              {/* Iter 60 — Scoped versioning: Videos use in-place Replace
                  (binary overwrite in the Bunny video slot to save storage);
                  Photos/PDFs use Upload New Version (creates a sibling asset
                  in the same version_group). Show only the button that
                  matches the asset kind so admins never see a disabled path. */}
              {asset.kind === "video" && (
                <button
                  data-testid="replace-asset-btn"
                  onClick={() => setShowReplace(true)}
                  className="w-full bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5"
                  title="Upload a new version of this video. Existing share links keep working."
                >
                  <Replace className="w-3.5 h-3.5 text-[var(--brand-accent)]" /> Replace video
                </button>
              )}
              {/* Iter 50 — Upload New Version. Creates a sibling asset in
                  the same version_group and inherits title/description/tags. */}
              {(asset.kind === "photo" || asset.kind === "pdf") && (
                <>
                  <input
                    ref={versionFileInputRef}
                    data-testid="version-file-input"
                    type="file"
                    accept={asset.kind === "photo" ? "image/*" : "application/pdf"}
                    onChange={handleVersionFileChange}
                    className="hidden"
                  />
                  <button
                    data-testid="upload-new-version-btn"
                    onClick={handleUploadNewVersionClick}
                    disabled={uploadingVersion}
                    className="w-full bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Upload a new version. Metadata (title, description, tags) is inherited from this version."
                  >
                    {uploadingVersion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5 text-[var(--brand-accent)]" />}
                    {uploadingVersion ? "Uploading…" : "Upload new version"}
                  </button>
                </>
              )}
              <div data-asset-dl-menu>
                <button
                  data-testid="asset-download-btn"
                  onClick={asset.kind === "pdf" ? handlePdfDirectDownload : loadDownloadQualities}
                  disabled={dlBusy || !!dlSaving.pdf || (asset.kind === "video" && asset.status !== "ready")}
                  className="w-full bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={asset.kind === "video" && asset.status !== "ready" ? "Video must be processed before download" : `Download this ${asset.kind || "video"}`}
                >
                  {(dlBusy || dlSaving.pdf) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 text-[var(--brand-accent)]" />}
                  {dlSaving.pdf ? "Saving…" : (asset.kind === "photo" ? "Download photo" : asset.kind === "pdf" ? "Download PDF" : "Download video")}
                </button>
                {dlOpen && (
                  <div className="mt-2 bg-zinc-950 border border-zinc-800 rounded-md overflow-hidden">
                    {dlError ? (
                      <div className="text-[11px] text-red-300 px-3 py-2">{dlError}</div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                          <span>Choose quality</span>
                          <button
                            onClick={() => setDlOpen(false)}
                            className="text-zinc-600 hover:text-zinc-300 transition"
                            title="Close"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        {dlQualities?.length ? dlQualities.map((q) => {
                          const saving = !!dlSaving[q.resolution];
                          const isVideo = asset.kind === "video";
                          // Videos: keep <a href> — the URL is a signed API endpoint
                          // that streams from Bunny with Content-Disposition already set;
                          // the browser handles the save natively. Photos: force-download
                          // via fetch-to-blob so the file lands with the right filename
                          // instead of opening in a new tab.
                          if (isVideo) {
                            return (
                              <a
                                key={q.resolution}
                                href={q.url}
                                data-testid={`asset-download-${q.resolution}`}
                                rel="noopener"
                                onClick={() => setTimeout(() => setDlOpen(false), 200)}
                                className={`flex items-center justify-between px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900 hover:text-[var(--brand-accent)] transition no-underline ${q.original ? "border-b border-zinc-900 bg-zinc-900/40" : ""}`}
                              >
                                <span className="flex items-center gap-2"><Download className="w-3 h-3 opacity-60" />{q.label}</span>
                                {q.original ? (
                                  <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded">Original</span>
                                ) : q.default ? (
                                  <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80">Best</span>
                                ) : null}
                              </a>
                            );
                          }
                          return (
                            <button
                              key={q.resolution}
                              type="button"
                              data-testid={`asset-download-${q.resolution}`}
                              onClick={() => handleDirectDownload(q)}
                              disabled={saving}
                              className={`w-full flex items-center justify-between px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900 hover:text-[var(--brand-accent)] transition text-left disabled:opacity-70 ${q.original ? "border-b border-zinc-900 bg-zinc-900/40" : ""}`}
                            >
                              <span className="flex items-center gap-2">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin text-[var(--brand-accent)]" /> : <Download className="w-3 h-3 opacity-60" />}
                                {saving ? "Saving…" : q.label}
                              </span>
                              {q.original ? (
                                <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded">Original</span>
                              ) : q.default ? (
                                <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80">Best</span>
                              ) : null}
                            </button>
                          );
                        }) : (
                          <div className="text-[11px] text-zinc-500 px-3 py-2">No qualities available.</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {asset.kind === "video" && (
                <>
                  <button
                    data-testid={TID.embedHealthBtn}
                    onClick={runEmbedHealth}
                    disabled={healthState?.loading}
                    className="w-full bg-zinc-900 border border-zinc-800 hover:border-[var(--brand-accent)]/40 rounded-md py-2 text-xs transition flex items-center justify-center gap-1.5 disabled:opacity-60"
                    title="Ping Bunny + verify the embed iframe still works + sync thumbnail filename"
                  >
                    {healthState?.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5 text-[var(--brand-accent)]" />}
                    Refresh from Bunny
                  </button>
                  {healthState && !healthState.loading && (
                    <div
                      data-testid={TID.embedHealthResult}
                      className={`text-[11px] border rounded-md p-2.5 leading-relaxed ${
                        healthState.status === "ok"
                          ? "border-emerald-900/40 bg-emerald-950/20 text-emerald-200"
                          : "border-amber-900/40 bg-amber-950/20 text-amber-200"
                      }`}
                    >
                      <div className="font-medium uppercase tracking-wider text-[10px] mb-1">
                        {healthState.status === "ok" ? "✓ Healthy" : `⚠ ${healthState.status}`}
                      </div>
                      <div>{healthState.hint}</div>
                      <div className="mt-1 text-zinc-500 text-[10px]">
                        bunny: {healthState.bunny_status_code ?? "—"} · embed: {healthState.embed_http_code ?? "—"}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="flex gap-2">
                <button
                  data-testid={TID.saveAssetBtn}
                  onClick={save}
                  disabled={saving}
                  className="flex-1 bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save
                </button>
                <button
                  data-testid={TID.deleteAssetBtn}
                  onClick={remove}
                  className="px-3 py-2 border border-red-900/40 text-red-400 rounded-md text-sm hover:bg-red-950/30 transition flex items-center justify-center"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {isClient && (
            <div className="pt-4 border-t border-zinc-800/60">
              <button
                data-testid="client-done-btn"
                onClick={onClose}
                className="w-full bg-[var(--brand-accent)] text-black font-semibold rounded-md py-2.5 text-sm hover:bg-[var(--brand-accent)]/90 transition flex items-center justify-center gap-2 shadow-lg shadow-[var(--brand-accent)]/10"
              >
                <CheckCircle2 className="w-4 h-4" /> Done
              </button>
              <p className="text-[10px] text-zinc-500 text-center mt-2 leading-relaxed">
                Tags save automatically as you add or remove them.
              </p>
            </div>
          )}
        </div>
      </div>

      {showShare && (
        <ShareModal
          target={{ kind: "asset", id: asset.id, title: title || asset.title }}
          onClose={() => setShowShare(false)}
        />
      )}
      {showReplace && asset && (
        <UploadModal
          replaceAssetId={asset.id}
          replaceAssetTitle={asset.title}
          onClose={() => setShowReplace(false)}
          onUploaded={() => { setShowReplace(false); onChange?.(); onClose?.(); }}
        />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-300">{value}</span>
    </div>
  );
}

function AgencyComment({ c, replies = [], canEdit, assetId, activeVersionNumber, onJumpToPage, onSeekAndPause, onDelete, onToggleResolved, onReplied, onHighlight }) {
  const approvalLabel = {
    approved: { text: "Approved", color: "text-emerald-400 border-emerald-900/50 bg-emerald-950/30", icon: ThumbsUp },
    rejected: { text: "Rejected", color: "text-red-400 border-red-900/50 bg-red-950/30", icon: ThumbsDown },
    "changes-requested": { text: "Changes requested", color: "text-amber-400 border-amber-900/50 bg-amber-950/30", icon: RotateCcw },
  }[c.approval];
  const ts = fmtTs(c.timestamp_sec);
  const showResolve = canEdit;

  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [notifyClient, setNotifyClient] = useState(true);
  const [replySending, setReplySending] = useState(false);
  const canNotify = Boolean(c.commenter_email);

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      await api.post(`/assets/${assetId}/reply`, {
        parent_comment_id: c.id,
        text: replyText.trim(),
        notify: canNotify && notifyClient,
      });
      setReplyText("");
      setReplying(false);
      onReplied?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not post reply");
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className={`group text-xs ${c.resolved ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-medium text-zinc-300 shrink-0">
          {c.commenter_name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-zinc-200 font-medium truncate">{c.commenter_name}</span>
            <span className="text-zinc-600 text-[10px] truncate">{c.commenter_email}</span>
            {/* Iter 61 — Admin timestamp chip. Previously a plain <span> with
                no onClick — hence "timestamp click broken in Admin View". Now
                a proper button wired to the same hardened seekAndPauseVideo
                path the client ShareView uses. */}
            {ts && (
              <button
                type="button"
                data-testid={`agency-anchor-timestamp-${c.id}`}
                onClick={() => { if (typeof c.timestamp_sec === "number") onSeekAndPause?.(c.timestamp_sec); }}
                disabled={typeof c.timestamp_sec !== "number"}
                title={typeof c.timestamp_sec === "number" ? `Seek to ${ts}` : undefined}
                className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/60 transition rounded-full px-1.5 py-0.5 font-mono disabled:cursor-default disabled:hover:bg-[var(--brand-accent)]/10 disabled:hover:border-[var(--brand-accent)]/30"
              >
                <Clock className="w-2.5 h-2.5" />{ts}
              </button>
            )}
            {/* Iter 47 — PDF page-anchor pill mirrors the video timestamp pill. */}
            {c.page_number !== null && c.page_number !== undefined && (
              <button
                onClick={() => onJumpToPage?.(c.page_number)}
                data-testid={`agency-anchor-page-${c.id}`}
                className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/60 transition rounded-full px-1.5 py-0.5 font-medium uppercase tracking-wider"
                title={`Jump to page ${c.page_number}`}
              >
                <FileText className="w-2.5 h-2.5" />Page {c.page_number}
              </button>
            )}
            {/* Iter 51 — Origin-version badge. Only rendered when this comment
                came from a DIFFERENT version than the one currently in view. */}
            {c.version_number && activeVersionNumber && c.version_number !== activeVersionNumber && (
              <span
                data-testid={`comment-version-badge-v${c.version_number}`}
                className="inline-flex items-center gap-1 text-[10px] text-zinc-300 bg-zinc-900 border border-zinc-700 rounded-full px-1.5 py-0.5 font-semibold uppercase tracking-wider"
                title={`From version V${c.version_number}`}
              >
                V{c.version_number}
              </span>
            )}
            {/* Iter 52 / 53 / 54 — "Drawing" chip. Click:
                • PDF   → jumps to the anchored page + glows
                • Photo → just glows
                • Video → seeks the player to the timestamp, pauses, then glows */}
            {c.drawing?.strokes?.length > 0 && (
              <button
                type="button"
                data-testid={`agency-drawing-chip-${c.id}`}
                onClick={() => {
                  if (c.page_number) onJumpToPage?.(c.page_number);
                  if (typeof c.timestamp_sec === "number") onSeekAndPause?.(c.timestamp_sec);
                  onHighlight?.();
                }}
                className="inline-flex items-center gap-1 text-[10px] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 hover:bg-[var(--brand-accent)]/20 hover:border-[var(--brand-accent)]/60 transition rounded-full px-1.5 py-0.5 font-medium uppercase tracking-wider"
                title="Show this annotation"
              >
                <Pencil className="w-2.5 h-2.5" /> Drawing
              </button>
            )}
            {c.resolved && (
              <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-emerald-400 bg-emerald-950/30 border border-emerald-900/50 px-1.5 py-0.5 rounded-full">
                <CheckCircle2 className="w-2.5 h-2.5" /> Resolved
              </span>
            )}
          </div>
          {approvalLabel && (
            <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border mt-1 ${approvalLabel.color}`}>
              <approvalLabel.icon className="w-2.5 h-2.5" /> {approvalLabel.text}
            </div>
          )}
          {c.text && <div className="text-zinc-300 mt-1 leading-relaxed">{c.text}</div>}
          {c.resolved && c.resolved_by_name && (
            <div className="text-[10px] text-emerald-400/80 mt-1 flex items-center gap-1">
              <CheckCircle2 className="w-2.5 h-2.5" />
              Resolved by <span className="font-medium">{c.resolved_by_name}</span>
              {c.resolved_at && <span className="text-zinc-600">· {timeAgoShort(c.resolved_at)}</span>}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            {showResolve && (
              <button
                data-testid={TID.resolveCommentBtn(c.id)}
                onClick={onToggleResolved}
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border transition ${c.resolved ? "text-zinc-500 border-zinc-800 hover:border-zinc-600" : "text-emerald-400 border-emerald-900/50 hover:bg-emerald-950/40"}`}
                title={c.resolved ? "Mark as open again" : "Mark as resolved"}
              >
                <CheckCircle2 className="w-2.5 h-2.5" />
                {c.resolved ? "Mark open" : "Mark resolved"}
              </button>
            )}
            {canEdit && !replying && (
              <button
                data-testid={`reply-btn-${c.id}`}
                onClick={() => setReplying(true)}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border text-zinc-400 border-zinc-800 hover:border-[var(--brand-accent)]/40 hover:text-[var(--brand-accent)] transition"
              >
                <MessageSquare className="w-2.5 h-2.5" /> Reply
              </button>
            )}
          </div>

          {replying && (
            <div className="mt-2 pl-3 border-l-2 border-[var(--brand-accent)]/40 space-y-2">
              <textarea
                data-testid={`reply-textarea-${c.id}`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${c.commenter_name}…`}
                rows={2}
                autoFocus
                className="w-full bg-black/40 border border-zinc-800 rounded p-2 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60 resize-none"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className={`flex items-center gap-1.5 text-[10px] ${canNotify ? "text-zinc-400 cursor-pointer" : "text-zinc-600 cursor-not-allowed"}`} title={canNotify ? "Email the client about this reply" : "This commenter didn't leave an email"}>
                  <input
                    type="checkbox"
                    data-testid={`reply-notify-${c.id}`}
                    checked={canNotify && notifyClient}
                    disabled={!canNotify}
                    onChange={(e) => setNotifyClient(e.target.checked)}
                    className="w-3 h-3 accent-[var(--brand-accent)]"
                  />
                  Notify {c.commenter_name} via email
                </label>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => { setReplying(false); setReplyText(""); }}
                    className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300 px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendReply}
                    disabled={replySending || !replyText.trim()}
                    data-testid={`reply-send-${c.id}`}
                    className="text-[10px] uppercase tracking-wider bg-[var(--brand-accent)] text-black rounded px-2.5 py-1 hover:bg-[var(--brand-accent)]/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {replySending ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                    Send reply
                  </button>
                </div>
              </div>
            </div>
          )}

          {replies.length > 0 && (
            <div className="mt-3 pl-3 border-l-2 border-zinc-800 space-y-3">
              {replies.map((r) => (
                <ReplyRow key={r.id} r={r} canEdit={canEdit} onDelete={() => onDelete && (window.confirm("Delete this reply?") && api.delete(`/comments/${r.id}`).then(onReplied))} />
              ))}
            </div>
          )}
        </div>
        {canEdit && (
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition shrink-0"
            title="Delete comment (and its replies)"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function ReplyRow({ r, canEdit, onDelete }) {
  return (
    <div className="group text-xs flex items-start gap-2">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium shrink-0 ${r.is_admin_reply ? "bg-[var(--brand-accent)]/20 text-[var(--brand-accent)]" : "bg-zinc-800 text-zinc-300"}`}>
        {r.commenter_name?.[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-zinc-200 font-medium truncate">{r.commenter_name}</span>
          {r.is_admin_reply && (
            <span className="text-[9px] uppercase tracking-wider text-[var(--brand-accent)]/80 bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-1.5 py-0.5 rounded-full">Team</span>
          )}
          <span className="text-[10px] text-zinc-600">{timeAgoShort(r.created_at)}</span>
        </div>
        {r.text && <div className="text-zinc-300 mt-0.5 leading-relaxed">{r.text}</div>}
      </div>
      {canEdit && (
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition shrink-0"
          title="Delete reply"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
